'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import GameCard from './GameCard';
import PremiumModal from '@/components/PremiumModal';
import GameSetupModal from '@/components/GameSetupModal';
import { Category } from '@/components/CategorySelectModal';
import { Gamepad2, Beer, Zap, Heart } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getTranslations } from '@/lib/i18n';
import { useSubscription } from '@/hooks/useSubscription';

export interface Game {
  id: string;
  name: string;
  emoji: string;
  description: string;
  recommendation: string;
  premium: boolean;
  premiumCategory?: string;
  players: string;
  duration: string;
  tags: string[];
  color: string;
}

const GAMES: Game[] = [
  // Klassiker
  {
    id: 'game-impostor',
    name: 'Impostor',
    emoji: '🕵️',
    description: 'Einer unter euch lügt — findet den Verräter! Alle bekommen ein Wort, der Impostor keins.',
    recommendation: 'Perfekt für 5–10 Leute die sich gut kennen. Sorgt für Vertrauen-Chaos!',
    premium: false,
    players: '4–12',
    duration: '10–20 min',
    tags: ['Raten', 'Lügen', 'Strategie'],
    color: '#7C3AED',
  },
  {
    id: 'game-antwort-impostor',
    name: 'Antwort Impostor',
    emoji: '🎭',
    description: 'Alle beantworten dieselbe Frage — nur der Impostor bekommt eine andere! Wer fliegt auf?',
    recommendation: 'Perfekt für 4–10 Personen. Sorgt für Diskussionen und Lacher!',
    premium: false,
    players: '4–10',
    duration: '15–30 min',
    tags: ['Raten', 'Fragen', 'Impostor'],
    color: '#EC4899',
  },
  {
    id: 'game-flasche',
    name: 'Flaschendrehen',
    emoji: '🍾',
    description: 'Die animierte Flasche dreht sich — wen trifft sie? Wahrheit, Pflicht oder Kuss?',
    recommendation: 'Der Klassiker ab 4 Personen. Funktioniert immer, egal wie spät es ist.',
    premium: false,
    players: '3–10',
    duration: '20–60 min',
    tags: ['Klassiker', 'Zufall', 'Spaß'],
    color: '#22D3EE',
  },
  {
    id: 'game-wahrheit-pflicht',
    name: 'Wahrheit oder Pflicht',
    emoji: '🎭',
    description: 'Das Original — Wahrheit oder Pflicht? Mit freischaltbaren Spicy-Fragen.',
    recommendation: 'Ideal ab 3 Personen. Je mutiger die Gruppe, desto lustiger.',
    premium: false,
    players: '3–10',
    duration: '20–60 min',
    tags: ['Klassiker', 'Fragen'],
    color: '#F472B6',
  },
  {
    id: 'game-werwolf',
    name: 'Werwolf',
    emoji: '🐺',
    description: 'Dorf gegen Werwölfe — wer überlebt die Nacht? Mit Erzähler-Modus.',
    recommendation: 'Am besten ab 7 Personen für ein episches Spielerlebnis.',
    premium: false,
    players: '6–16',
    duration: '30–60 min',
    tags: ['Strategie', 'Rollen', 'Spannung'],
    color: '#8B5CF6',
  },
  {
    id: 'game-quiz',
    name: 'Party-Quiz',
    emoji: '🧠',
    description: 'Wer weiß am meisten? Kategorien von Popkultur bis Wissenschaft.',
    recommendation: 'Perfekt für Gruppen mit Wissenshunger. Macht auch nüchtern Spaß!',
    premium: false,
    players: '2–20',
    duration: '15–45 min',
    tags: ['Wissen', 'Quiz', 'Teams'],
    color: '#4ADE80',
  },
  {
    id: 'game-emoji-raten',
    name: 'Emoji Raten',
    emoji: '😂',
    description: 'Erkläre Begriffe nur mit Emojis — deine Mitspieler müssen raten! Wer ist der Emoji-König?',
    recommendation: 'Für alle Altersgruppen. Macht auf dem Handy besonders viel Spaß!',
    premium: false,
    players: '3–12',
    duration: '15–30 min',
    tags: ['Kreativ', 'Raten', 'Emojis'],
    color: '#F59E0B',
  },
  {
    id: 'game-luegen-detektiv',
    name: 'Lügen-Detektiv',
    emoji: '🔎',
    description: '2 Wahrheiten, 1 Lüge — finde die Lüge! Wer kennt seine Mitspieler am besten?',
    recommendation: 'Ideal zum Kennenlernen. Überraschungen garantiert!',
    premium: false,
    players: '3–15',
    duration: '15–40 min',
    tags: ['Lügen', 'Kennenlernen', 'Raten'],
    color: '#06B6D4',
  },
  {
    id: 'game-wer-bin-ich',
    name: 'Wer bin ich?',
    emoji: '🤔',
    description: 'Bekomme eine Persona auf die Stirn — finde durch Ja/Nein-Fragen heraus, wer du bist!',
    recommendation: 'Perfekt für 3–10 Personen. Klassiker der Partyspiele!',
    premium: false,
    players: '3–10',
    duration: '15–30 min',
    tags: ['Raten', 'Fragen', 'Klassiker'],
    color: '#10B981',
  },
  // Trinkspiele
  {
    id: 'game-busfahrer',
    name: 'Busfahrer',
    emoji: '🚌',
    description: 'Farbe, höher/tiefer, innen/außen — wer wird der Busfahrer?',
    recommendation: 'Das Warm-Up für jeden Abend. Perfekt für 3–8 Personen.',
    premium: false,
    players: '3–8',
    duration: '15–30 min',
    tags: ['Karten', 'Trinken', 'Klassiker'],
    color: '#F59E0B',
  },
  {
    id: 'game-kings-cup',
    name: "King's Cup",
    emoji: '👑',
    description: 'Karten ziehen, Regeln befolgen — wer zieht den letzten König?',
    recommendation: 'Der Partystart schlechthin. Je mehr Leute, desto besser!',
    premium: false,
    players: '4–12',
    duration: '30–90 min',
    tags: ['Karten', 'Regeln', 'Spaß'],
    color: '#EF4444',
  },
  {
    id: 'game-ich-hab-noch-nie',
    name: 'Ich hab noch nie',
    emoji: '🙈',
    description: 'Beichte deine Geheimnisse — oder trink! Mit lustigen Kategorien.',
    recommendation: 'Ideal zum Kennenlernen. Sorgt immer für Überraschungen.',
    premium: false,
    players: '3–15',
    duration: '20–40 min',
    tags: ['Beichten', 'Trinken', 'Kennenlernen'],
    color: '#22D3EE',
  },
  {
    id: 'game-spicy-trink',
    name: 'Spicy Trinkspiele',
    emoji: '🌶️',
    description: 'Heiße Fragen, pikante Aufgaben — nur für Mutige. 18+ Inhalte.',
    recommendation: 'Für Gruppen die sich sehr gut kennen. Sorgt für unvergessliche Momente.',
    premium: true,
    premiumCategory: 'Spicy',
    players: '3–10',
    duration: '30–90 min',
    tags: ['18+', 'Spicy', 'Mutig'],
    color: '#EF4444',
  },
  // Party-Chaos
  {
    id: 'game-pantomime',
    name: 'Pantomime Extrem',
    emoji: '🤸',
    description: 'Begriffe spielerisch darstellen — ohne Worte! Mit absurden Kategorien.',
    recommendation: 'Für Gruppen die keine Hemmungen haben. Garantierter Lachflash!',
    premium: false,
    players: '4–20',
    duration: '20–45 min',
    tags: ['Darstellen', 'Lachen', 'Teams'],
    color: '#F472B6',
  },
  {
    id: 'game-hot-seat',
    name: 'Hot Seat',
    emoji: '🔥',
    description: 'Einer sitzt auf dem heißen Stuhl — die Gruppe stellt Fragen. Kein Entrinnen.',
    recommendation: 'Ab 4 Personen. Perfekt wenn ihr mehr voneinander wissen wollt.',
    premium: false,
    players: '4–12',
    duration: '20–40 min',
    tags: ['Fragen', 'Persönlich', 'Spaß'],
    color: '#F59E0B',
  },
  {
    id: 'game-chaos-rules',
    name: 'Chaos-Regeln',
    emoji: '🌀',
    description: 'Jeder macht eine Regel — wer sie bricht, trinkt! Mit Partychaos-Kategorien.',
    recommendation: 'Für fortgeschrittene Party-Gruppen. Wird immer chaotischer!',
    premium: true,
    premiumCategory: 'Partychaos',
    players: '4–15',
    duration: '60–180 min',
    tags: ['Regeln', 'Chaos', 'Trinken'],
    color: '#8B5CF6',
  },
  {
    id: 'game-blind-date',
    name: 'Blind Date Challenge',
    emoji: '💘',
    description: 'Aufgaben für Paare und Singles — romantisch, witzig, chaotisch.',
    recommendation: 'Gemischte Gruppen, entspannte Atmosphäre. Nicht für Schüchterne!',
    premium: true,
    premiumCategory: 'Spicy',
    players: '4–12',
    duration: '30–60 min',
    tags: ['Romantik', 'Spicy', 'Spaß'],
    color: '#F472B6',
  },
];

const SECTIONS = [
  {
    id: 'klassiker',
    label: 'Klassiker',
    emoji: '🎮',
    description: 'Die zeitlosen Favoriten — funktionieren immer und überall.',
    icon: Gamepad2,
    gameIds: ['game-impostor', 'game-antwort-impostor', 'game-flasche', 'game-wahrheit-pflicht', 'game-werwolf', 'game-quiz', 'game-emoji-raten', 'game-luegen-detektiv', 'game-wer-bin-ich'],
    color: '#7C3AED',
  },
  {
    id: 'trinkspiele',
    label: 'Trinkspiele',
    emoji: '🍺',
    description: 'Für Abende mit Getränken — in Maßen genießen, 18+.',
    icon: Beer,
    gameIds: ['game-busfahrer', 'game-kings-cup', 'game-ich-hab-noch-nie', 'game-spicy-trink'],
    color: '#F59E0B',
  },
  {
    id: 'party-chaos',
    label: 'Party-Chaos',
    emoji: '🌀',
    description: 'Wenn die Party eskalieren soll — hier ist der Zündstoff.',
    icon: Zap,
    gameIds: ['game-pantomime', 'game-hot-seat', 'game-chaos-rules', 'game-blind-date'],
    color: '#F472B6',
  },
];

const FAVORITES_KEY = 'party_game_favorites';

type FlowStep = 'idle' | 'setup';

export default function GamesSection() {
  const router = useRouter();
  const { language } = useLanguage();
  const t = getTranslations(language);
  const gc = (t as any).gameContent;
  const [flowStep, setFlowStep] = useState<FlowStep>('idle');
  const [premiumModalOpen, setPremiumModalOpen] = useState(false);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [premiumTrigger, setPremiumTrigger] = useState<string | undefined>();
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const { isPremium, subscription } = useSubscription();
  const trialEndDate =
    subscription?.subscription_status === 'trialing' ? subscription?.current_period_end ?? null : null;

  // Build translated games list inside component so it reacts to language changes
  const GAMES_TRANSLATED = GAMES.map((game) => ({
    ...game,
    name: gc?.gameNames?.[game.id] ?? game.name,
    description: gc?.gameDescriptions?.[game.id] ?? game.description,
  }));

  useEffect(() => {
    const loadFavorites = () => {
      try {
        const stored = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
        setFavoriteIds(stored);
      } catch {
        setFavoriteIds([]);
      }
    };
    loadFavorites();
    // Refresh favorites when window gets focus (after returning from game setup)
    window.addEventListener('focus', loadFavorites);
    return () => window.removeEventListener('focus', loadFavorites);
  }, []);

  const favoriteGames = GAMES_TRANSLATED.filter((g) => favoriteIds.includes(g.id));

  const handleGameClick = (game: Game) => {
    if (game.premium && !isPremium) {
      setPremiumTrigger(game.premiumCategory);
      setPremiumModalOpen(true);
      return;
    }
    setSelectedGame(game);
    setFlowStep('setup');
  };

  const handleGameStart = (players: string[], category: Category, settings: { hintsEnabled: boolean; timeLimitEnabled: boolean; durationMinutes: number }) => {
    if (!selectedGame) return;
    setFlowStep('idle');
    const params = new URLSearchParams({
      game: selectedGame.id,
      name: selectedGame.name,
      emoji: selectedGame.emoji,
      color: selectedGame.color,
      category: category.id,
      players: players.join(','),
      hints: settings.hintsEnabled ? '1' : '0',
      timeLimit: settings.timeLimitEnabled ? '1' : '0',
      duration: String(settings.durationMinutes),
    });
    router.push(`/games?${params.toString()}`);
  };

  const handlePremiumRequired = () => {
    setFlowStep('idle');
    setPremiumTrigger(undefined);
    setPremiumModalOpen(true);
  };

  const handleClose = () => {
    setFlowStep('idle');
    setSelectedGame(null);
  };

  const SECTIONS_TRANSLATED = [
    {
      id: 'klassiker',
      label: t.games.klassiker,
      emoji: '🎮',
      description: 'Die zeitlosen Favoriten — funktionieren immer und überall.',
      icon: Gamepad2,
      gameIds: ['game-impostor', 'game-antwort-impostor', 'game-flasche', 'game-wahrheit-pflicht', 'game-werwolf', 'game-quiz', 'game-emoji-raten', 'game-luegen-detektiv', 'game-wer-bin-ich'],
      color: '#7C3AED',
    },
    {
      id: 'trinkspiele',
      label: t.games.trinkspiele,
      emoji: '🍺',
      description: 'Für Abende mit Getränken — in Maßen genießen, 18+.',
      icon: Beer,
      gameIds: ['game-busfahrer', 'game-kings-cup', 'game-ich-hab-noch-nie', 'game-spicy-trink'],
      color: '#F59E0B',
    },
    {
      id: 'party-chaos',
      label: t.games.partyChaos,
      emoji: '🌀',
      description: 'Wenn die Party eskalieren soll — hier ist der Zündstoff.',
      icon: Zap,
      gameIds: ['game-pantomime', 'game-hot-seat', 'game-chaos-rules', 'game-blind-date'],
      color: '#F472B6',
    },
  ];

  return (
    <>
      <section id="games" className="py-20 max-w-screen-2xl mx-auto px-6 lg:px-10 xl:px-16">
        {/* Section Intro */}
        <div className="text-center mb-16">
          <p className="section-header mb-3">{t.games.allGames}</p>
          <h2 className="font-extrabold text-4xl md:text-5xl mb-4">
            <span className="text-gradient-primary">17 {t.games.allGames}</span>
            <span style={{ color: 'var(--foreground)' }}> für jeden Abend</span>
          </h2>
          <p className="text-lg max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
            Von entspannt bis chaotisch — für jede Stimmung das passende Spiel.
          </p>
        </div>

        {/* Favorites Section */}
        {favoriteGames.length > 0 && (
          <div className="mb-20">
            <div className="flex items-center gap-4 mb-8">
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(255,107,107,0.15)', border: '1px solid rgba(255,107,107,0.3)' }}>
                <Heart size={22} style={{ color: '#FF6B6B', fill: '#FF6B6B' }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">❤️</span>
                  <h3 className="font-extrabold text-2xl">{t.games.myFavorites}</h3>
                </div>
                <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Deine gespeicherten Lieblingsspiele
                </p>
              </div>
              <div
                className="ml-auto h-px flex-1 max-w-xs"
                style={{ background: 'linear-gradient(90deg, rgba(255,107,107,0.4), transparent)' }}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
              {favoriteGames.map((game) => (
                <GameCard
                  key={`fav-${game.id}`}
                  game={game}
                  onClick={() => handleGameClick(game)}
                  isPremium={isPremium}
                  trialEndDate={trialEndDate}
                />
              ))}
            </div>
          </div>
        )}

        {/* Game Sections */}
        <div className="space-y-20">
          {SECTIONS_TRANSLATED.map((section) => {
            const sectionGames = GAMES_TRANSLATED.filter((g) => section.gameIds.includes(g.id));
            const SectionIcon = section.icon;

            return (
              <div key={`section-${section.id}`}>
                {/* Section Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${section.color}20`, border: `1px solid ${section.color}40` }}
                  >
                    <SectionIcon size={22} style={{ color: section.color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{section.emoji}</span>
                      <h3 className="font-extrabold text-2xl">{section.label}</h3>
                    </div>
                    <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      {section.description}
                    </p>
                  </div>
                  <div
                    className="ml-auto h-px flex-1 max-w-xs"
                    style={{ background: `linear-gradient(90deg, ${section.color}40, transparent)` }}
                  />
                </div>

                {/* Game Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-5">
                  {sectionGames.map((game) => (
                    <GameCard
                      key={game.id}
                      game={game}
                      onClick={() => handleGameClick(game)}
                      isPremium={isPremium}
                      trialEndDate={trialEndDate}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Combined Setup Modal: Players + Category on one screen */}
      <GameSetupModal
        open={flowStep === 'setup'}
        onClose={handleClose}
        onStart={handleGameStart}
        onPremiumRequired={handlePremiumRequired}
        gameName={selectedGame?.name ?? ''}
        gameId={selectedGame?.id ?? ''}
        gameColor={selectedGame?.color ?? '#7C3AED'}
        gameEmoji={selectedGame?.emoji ?? '🎮'}
      />

      <PremiumModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        triggerCategory={premiumTrigger}
      />
    </>
  );
}